// Design system constants for Eventi Vendor App
// This file centralizes all design tokens to ensure consistency

export const DESIGN_TOKENS = {
  colors: {
    primary: '#67314f',
    primaryDark: '#5a2a42',
    primaryLight: '#4d2238',
    text: '#181818',
    textSecondary: '#93989c',
    background: '#ffffff',
    backgroundPaper: '#f4dbc5',
    backgroundInput: '#f6f8f8',
    white: '#ffffff',
    error: '#d32f2f',
    success: '#2e7d32',
    warning: '#ed6c02',
  },
  typography: {
    fontFamily: '"HT Mosafer Rounded", "Inter", sans-serif',
    fontFamilyFallback: '"Inter", sans-serif',
    fontWeights: {
      normal: 400,
      medium: 500,
      bold: 700,
    },
    fontSizes: {
      h1: '44px',
      h2: '28px',
      h3: '22px',
      h4: '18px',
      body1: '16px',
      body2: '14px',
      caption: '13px',
    },
    lineHeights: {
      tight: 1.15,
      normal: 1.2,
      relaxed: 1.4,
    },
    letterSpacing: {
      tight: '-0.44px',
      normal: '-0.28px',
      relaxed: '-0.11px',
    },
  },
  spacing: {
    borderRadius: '16px',
    borderRadiusSmall: '12px',
    borderRadiusLarge: '20px',
    borderRadiusXLarge: '24px',
  },
  shadows: {
    card: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cardHover: '0 8px 25px rgba(0, 0, 0, 0.15)',
    button: '0 4px 8px rgba(103, 49, 79, 0.3)',
    buttonHover: '0 6px 12px rgba(103, 49, 79, 0.4)',
    input: 'none',
  },
  transitions: {
    smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 0.2s ease',
    slow: 'all 0.4s ease',
  },
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },
} as const;

// Helper functions for common styling patterns
export const getButtonStyles = (variant: 'primary' | 'secondary' | 'outlined') => {
  const base = {
    borderRadius: DESIGN_TOKENS.spacing.borderRadius,
    fontFamily: DESIGN_TOKENS.typography.fontFamily,
    fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
    fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
    textTransform: 'none' as const,
    transition: DESIGN_TOKENS.transitions.smooth,
  };

  switch (variant) {
    case 'primary':
      return {
        ...base,
        backgroundColor: DESIGN_TOKENS.colors.primary,
        color: DESIGN_TOKENS.colors.white,
        boxShadow: DESIGN_TOKENS.shadows.button,
        '&:hover': {
          backgroundColor: DESIGN_TOKENS.colors.primaryDark,
          boxShadow: DESIGN_TOKENS.shadows.buttonHover,
          transform: 'translateY(-1px)',
        },
        '&:disabled': {
          backgroundColor: DESIGN_TOKENS.colors.textSecondary,
          color: DESIGN_TOKENS.colors.white,
        },
      };
    case 'secondary':
      return {
        ...base,
        backgroundColor: 'transparent',
        color: DESIGN_TOKENS.colors.primary,
        '&:hover': {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
        },
      };
    case 'outlined':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: `1px solid ${DESIGN_TOKENS.colors.primary}`,
        color: DESIGN_TOKENS.colors.primary,
        '&:hover': {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
          borderColor: DESIGN_TOKENS.colors.primary,
        },
      };
    default:
      return base;
  }
};

export const getFormFieldStyles = () => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: DESIGN_TOKENS.colors.backgroundInput,
    borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
    height: '56px',
    transition: DESIGN_TOKENS.transitions.fast,
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input': {
    fontFamily: DESIGN_TOKENS.typography.fontFamily,
    fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
    color: DESIGN_TOKENS.colors.text,
    padding: '16px 14px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: DESIGN_TOKENS.typography.fontFamily,
    color: DESIGN_TOKENS.colors.textSecondary,
    fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  },
});

export const getCardStyles = () => ({
  borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
  boxShadow: DESIGN_TOKENS.shadows.card,
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: DESIGN_TOKENS.transitions.smooth,
  '&:hover': {
    boxShadow: DESIGN_TOKENS.shadows.cardHover,
    transform: 'translateY(-4px)',
  },
});

export const getTypographyStyles = (variant: 'h1' | 'h2' | 'h3' | 'h4' | 'body1' | 'body2' | 'caption') => {
  const base = {
    fontFamily: DESIGN_TOKENS.typography.fontFamily,
    color: DESIGN_TOKENS.colors.text,
    margin: 0,
  };

  switch (variant) {
    case 'h1':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.h1,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.tight,
        letterSpacing: DESIGN_TOKENS.typography.letterSpacing.tight,
      };
    case 'h2':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.h2,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.tight,
        letterSpacing: DESIGN_TOKENS.typography.letterSpacing.normal,
      };
    case 'h3':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.h3,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.normal,
        letterSpacing: DESIGN_TOKENS.typography.letterSpacing.relaxed,
      };
    case 'h4':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.h4,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.normal,
        letterSpacing: '-0.18px',
      };
    case 'body1':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
      };
    case 'body2':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.body2,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
      };
    case 'caption':
      return {
        ...base,
        fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
        fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
        lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
        letterSpacing: '-0.08px',
      };
    default:
      return base;
  }
};

// Export individual tokens for direct access
export const { colors, typography, spacing, shadows, transitions, breakpoints } = DESIGN_TOKENS;
